import type { ProjectFilterActions } from "@/features/projects/filter/projectFilterReducer";
import styled from "@emotion/styled";
import {
  type OpportunitiesSearchFilters,
  type ProjectCategorisation,
} from "@repo/api";
import { Badge, Pill, Text, Variables } from "@repo/skapa";
import {
  DEFAULT_LOCALE,
  getLanguageAndLocaleFromUrl,
  t,
} from "@repo/translation";
import difference from "lodash/difference";

interface CategoriesProps {
  categories: ProjectCategorisation[];
  actions: ProjectFilterActions;
  filters: OpportunitiesSearchFilters;
}

const Label = styled.span`
  align-items: center;
  display: flex;
  gap: ${Variables.space25};
`;

const List = styled.div`
  display: flex;
  gap: ${Variables.space100};
`;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${Variables.space100};
`;

const isSelected = (
  selectedProjectIds: string[] | undefined,
  categoryProjectIds: string[]
) => {
  if (selectedProjectIds === undefined) {
    return false;
  }

  if (selectedProjectIds.length === 0) {
    return false;
  }

  if (selectedProjectIds.length !== categoryProjectIds.length) {
    return false;
  }

  return difference(selectedProjectIds, categoryProjectIds).length === 0;
};

export const Categories = ({
  categories,
  actions,
  filters,
}: CategoriesProps) => {
  const locale = getLanguageAndLocaleFromUrl();
  const { language } = locale ?? DEFAULT_LOCALE;

  return (
    <Container>
      <Text textStyle="Label.S">{t("label.ai_categories_title")}</Text>
      <List>
        {categories.map((category) => {
          const isPillSelected = isSelected(
            filters.projectIds,
            category.projectIds
          );

          return (
            <Pill
              key={category.category[language]}
              label={
                <Label>
                  {category.category[language]}
                  <Badge
                    label={category.projectIds.length}
                    size="small"
                    colour="black"
                  />
                </Label>
              }
              selected={isPillSelected}
              size="small"
              onClick={() => {
                isPillSelected
                  ? actions.setProjectIds([])
                  : actions.setProjectIds(category.projectIds);
              }}
            />
          );
        })}
      </List>
    </Container>
  );
};
